import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Envelope, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.toLowerCase().trim() });
      setSent(true);
    } catch (err) {
      // Backend returns generic 200 even on unknown email, so this branch
      // only fires on genuine validation/transport failures
      toast.error(err?.response?.data?.detail || "Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" data-testid="forgot-password-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF3B6B]/20 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00E5FF]/10 blur-[100px] rounded-full" />
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="forgot-logo">
          <div className="w-9 h-9 rounded-md bg-[#FF3B6B] flex items-center justify-center shadow-[0_0_24px_-4px_#FF3B6B]">
            <Lightning weight="fill" className="text-white" size={20} />
          </div>
          <span className="font-semibold text-xl">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
        </Link>
        <div className="glass-strong rounded-2xl p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent-state">
              <CheckCircle size={48} weight="duotone" className="mx-auto text-[#00E5FF] mb-4" />
              <h1 className="text-2xl font-bold tracking-tighter">Check your inbox</h1>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">
                If an account exists for <span className="text-white font-medium">{email}</span>, a reset link is on its way.
              </p>
              <p className="text-xs text-white/40 mt-2">
                The link expires in 30 minutes. Check spam if you don't see it.
              </p>
              <Link to="/auth" className="inline-flex items-center gap-2 mt-6 text-sm text-[#FF3B6B] hover:underline" data-testid="forgot-back-link">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tighter">Reset your password</h1>
              <p className="text-sm text-white/50 mt-1">Enter your email and we'll send a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">Email</label>
                  <div className="relative">
                    <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <button
                  disabled={loading}
                  type="submit"
                  className="w-full btn-primary py-3 rounded-lg font-semibold disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending..." : "Send reset link"}
                </button>
              </form>
              <Link to="/auth" className="inline-flex items-center gap-2 mt-6 text-sm text-white/50 hover:text-white" data-testid="forgot-back-to-signin">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
